// Default track-cover artwork shown when a track has no Apple Music
// artwork (vinyl-only / old underground / no-Apple-match releases).
// Inline SVG data-URI — no asset file needed, scales to any cover slot.
//
// Design: the quiet "no artwork" look used across the app (TrackArt.scss,
// Mixpedia's empty art): a FAINT cyan→purple tint (12%) with a grey ♫.
// Semi-transparent on purpose — it takes on whatever it sits on, so one image
// works on the dark and the light theme. (Was a full-strength brand gradient
// with a white ♫: tracks without art became the loudest thing in a list.)
// `dominant-baseline="central"` does the vertical centring.
const _SVG = (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64">`
  + `<defs>`
  +   `<linearGradient id="bg" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">`
  +     `<stop offset="0%" stop-color="#00dce8" stop-opacity="0.12"/>`
  +     `<stop offset="100%" stop-color="#a855f7" stop-opacity="0.12"/>`
  +   `</linearGradient>`
  + `</defs>`
  + `<rect width="64" height="64" fill="url(#bg)"/>`
  + `<text x="32" y="32" text-anchor="middle" dominant-baseline="central" `
  +       `font-family="Georgia, serif" font-size="24" fill="#888888" fill-opacity="0.9">♫</text>`
  + `</svg>`
);
// encodeURIComponent escapes `#`, `"`, `<`, `>`, etc. — but NOT `(`
// and `)` (those are RFC 3986 "marks", left unescaped). The jinke
// bottom player renders covers via CSS `background-image: url(...)`,
// and CSS url() terminates at the first unescaped `)`. So we also
// post-encode parens. Without this, any SVG containing `rotate(...)`,
// `translate(...)`, or `url(#id)` truncates mid-string and renders
// nothing.
const _encode = (s) => encodeURIComponent(s)
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29');
export const DEFAULT_TRACK_COVER = `data:image/svg+xml;utf8,${_encode(_SVG)}`;

// Playlist counterpart: same brand gradient (so a cover-less playlist reads
// as "MixGoat playlist", not "broken image") with a list-of-tracks glyph.
// Used wherever playlists are listed with a thumbnail (playlist sidebar,
// Add-to-playlist sheet). Cover-less playlists were rendering as a black
// square on phones — the cloud row had no cover_image and the placeholder
// was a barely visible glyph on the panel colour.
const _PLAYLIST_SVG = (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64">`
  + `<defs>`
  +   `<linearGradient id="pbg" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">`
  +     `<stop offset="0%" stop-color="#1f2a44"/>`
  +     `<stop offset="55%" stop-color="#2b2f6b"/>`
  +     `<stop offset="100%" stop-color="#4a3aff"/>`
  +   `</linearGradient>`
  + `</defs>`
  + `<rect width="64" height="64" fill="url(#pbg)"/>`
  + `<g stroke="#ffffff" stroke-opacity="0.92" stroke-width="3.2" stroke-linecap="round" fill="none">`
  +   `<line x1="17" y1="22" x2="43" y2="22"/>`
  +   `<line x1="17" y1="32" x2="43" y2="32"/>`
  +   `<line x1="17" y1="42" x2="31" y2="42"/>`
  + `</g>`
  + `<circle cx="42" cy="44" r="4.2" fill="#ffffff" fill-opacity="0.92"/>`
  + `<line x1="46" y1="44" x2="46" y2="31" stroke="#ffffff" stroke-opacity="0.92" stroke-width="3.2" stroke-linecap="round"/>`
  + `</svg>`
);
export const DEFAULT_PLAYLIST_COVER = `data:image/svg+xml;utf8,${_encode(_PLAYLIST_SVG)}`;

// Small-thumbnail variant of a cover URL for dense list rows. Only the
// app's own /album-art?id= route understands the `s` param (it serves a
// 64px JPEG cached on disk instead of the full-size embedded art, which
// can be several MB); any other cover source passes through untouched.
export function artThumbUrl(cover) {
    if (typeof cover === 'string' && cover.includes('/album-art?id=')) return `${cover}&s=1`;
    return cover || null;
}

// Picks the first non-empty cover URL from a list of candidates.
// Returns DEFAULT_TRACK_COVER if every candidate is null/undefined/empty.
// Use this everywhere we dispatch to addTrack / addTrackToQueue so the
// bottom player always shows something instead of an empty slot.
export function resolveCover(...candidates) {
    for (const c of candidates) {
        if (typeof c === 'string' && c.trim()) return c.trim();
    }
    return DEFAULT_TRACK_COVER;
}

// Swap an artwork URL up to a higher resolution, for surfaces that render it
// large: the now-playing hero (~380px) and the OS lock screen (~512px). A
// 100px thumbnail stretched to either looks visibly soft.
//
// This lived in three places with three different regexes — NowPlayingPage's
// (thorough), MusicPlayer's mediaSession one (which missed Apple's
// `100x100bb-60.jpg` compression suffix entirely, so the lock screen kept
// showing the thumbnail), and the queue builder's. One copy now.
//
// IMPORTANT: the upscaled URL is a DIFFERENT resource from the thumbnail the
// feed already fetched, so it is always a cold network request. Anything
// rendering it should either preload it (see prefetchArtwork) or show the
// cached thumbnail first — otherwise the slot is empty until it lands.
export function upscaleArtwork(url) {
    if (!url || typeof url !== 'string') return url;
    let out = url;

    // SoundCloud encodes the size as a filename suffix.
    out = out
        .replace('-small.', '-t500x500.')
        .replace('-tiny.', '-t500x500.')
        .replace('-mini.', '-t500x500.')
        .replace('-badge.', '-t500x500.')
        .replace('-large.', '-t500x500.')
        .replace('-t67x67.', '-t500x500.')
        .replace('-t120x120.', '-t500x500.')
        .replace('-t200x200.', '-t500x500.')
        .replace('-t300x300.', '-t500x500.');

    // Apple Music / iTunes (mzstatic): the size lives in the filename, with an
    // optional `-60`-style compression suffix — `.../100x100bb.jpg`,
    // `.../100x100bb-60.jpg`.
    if (out.includes('mzstatic.com')) {
        // `ac` is Apple's artist-picture crop (…/200x200ac.jpg); it keeps its own suffix
        out = out.replace(/\/\d+x\d+(bb|ac)(-\d+)?\.jpg/i, '/600x600$1.jpg');
        out = out.replace(/\/\d+x\d+(bb|ac)(-\d+)?\.png/i, '/600x600$1.png');
    }

    return out;
}

// Cover for a playlist row on ANY surface: the playlist's own cover, else its
// first track's cover, else the default playlist artwork. On web/native a
// localhost:3069 (desktop album-art server) URL can't load, so it's skipped.
export function playlistCoverUrl(p) {
    if (!p) return DEFAULT_PLAYLIST_COVER;
    const usable = (u) => {
        if (!u || typeof u !== 'string') return '';
        const isWebLike = typeof window !== 'undefined' && window.electron && !window.electron.db;
        if (isWebLike && u.startsWith('http://localhost:3069/')) return '';
        return u;
    };
    const own = usable(p.cover_image || p.coverImage || p.cover);
    if (own) return own;
    const firstWithCover = (p.tracks || []).find(t => (t && t.track && t.track.cover) || (t && t.cover));
    const first = firstWithCover ? usable((firstWithCover.track && firstWithCover.track.cover) || firstWithCover.cover) : '';
    return first || DEFAULT_PLAYLIST_COVER;
}

// Remote art URLs carry their size (…/200x200bb.jpg). Ask for the size the
// slot needs so a 700px hero never upscales a 200px thumbnail. Sizes: 100 /
// 200 / 400 / 800. Unknown URL shapes come back unchanged.
export function coverAt(url, px) {
    if (typeof url !== 'string') return url;
    const size = px <= 100 ? 100 : px <= 200 ? 200 : px <= 400 ? 400 : 800;
    if (/sndcdn\.com/.test(url)) {
        const sc = px <= 200 ? 't300x300' : 't500x500';
        return url.replace(/-(small|tiny|mini|badge|large|t\d+x\d+)\./, `-${sc}.`);
    }
    return url.replace(/\/(\d{2,4})x(\d{2,4})(bb|cc|sr|ac)?(-\d+)?\.(jpg|jpeg|png|webp)(\?|$)/i, `/${size}x${size}$3$4.$5$6`);
}
